<template>
    <div class="w-full">
        <div v-if="isProcessing" class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex items-center gap-2 text-sm text-slate-500">
                <UIcon name="i-heroicons-arrow-path" class="h-4 w-4 animate-spin text-blue-500" />
                <span>Generating creative bets...</span>
            </div>
        </div>

        <div v-else-if="hasError" class="rounded-xl border border-red-200 bg-red-50 p-4">
            <div class="flex items-center gap-2 text-sm text-red-700">
                <UIcon name="i-heroicons-x-circle" class="h-4 w-4" />
                <span>{{ part.errorText || 'Failed to generate creative bets' }}</span>
            </div>
        </div>

        <div v-else-if="toolData && activeBet" class="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 shadow-sm">
            <div class="mb-3 flex flex-col gap-3 px-1 sm:flex-row sm:items-start sm:justify-between">
                <div class="min-w-0">
                    <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-700">
                        <UIcon name="i-heroicons-sparkles" class="h-4 w-4" />
                        Creative bets
                    </div>
                    <p class="mt-1 text-sm leading-5 text-slate-700">{{ compactSummary }}</p>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                    <span class="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                        {{ activeIndex + 1 }} of {{ toolData.bets.length }}
                    </span>
                    <button
                        v-if="hasMultipleBets"
                        type="button"
                        class="inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-purple-700"
                        @click="showNextBet"
                    >
                        Review next bet
                        <UIcon name="i-heroicons-arrow-right" class="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>

            <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                        <div class="flex flex-wrap items-center gap-2">
                            <span class="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700 ring-1 ring-purple-100">
                                Bet: {{ activeBet.label }}
                            </span>
                            <span v-if="activeBet.recommended" class="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                                Recommended
                            </span>
                            <span v-if="activeBet.focus" class="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">
                                Focus: {{ focusLabel(activeBet.focus) }}
                            </span>
                        </div>
                    </div>
                    <div class="flex shrink-0 flex-col items-end gap-2">
                        <button
                            type="button"
                            class="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-slate-800"
                            @click="emit('draft-bet', activeBet, toolData)"
                        >
                            Draft this bet
                            <UIcon name="i-heroicons-pencil-square" class="h-3.5 w-3.5" />
                        </button>
                        <button
                            v-if="isActiveBetStep1Eligible"
                            type="button"
                            class="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors"
                            :class="isActiveBetSelected ? 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100' : 'border-slate-200 bg-white text-slate-900 hover:bg-slate-50'"
                            :aria-pressed="isActiveBetSelected"
                            @click="toggleActiveBetSelection"
                        >
                            <UIcon :name="isActiveBetSelected ? 'i-heroicons-check-circle' : 'i-heroicons-plus-circle'" class="h-3.5 w-3.5" />
                            {{ isActiveBetSelected ? 'Selected for Step 1 A/B' : 'Add to Step 1 A/B' }}
                        </button>
                        <div v-else class="max-w-52 text-right text-[11px] leading-4 text-slate-500">
                            Followup-only bet: draft individually, not as a Step 1 A/B variant.
                        </div>
                        <button
                            type="button"
                            class="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                            @click="emit('give-feedback', activeBet, toolData)"
                        >
                            Give feedback on this bet
                            <UIcon name="i-heroicons-chat-bubble-left-ellipsis" class="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>

                <div class="mt-3 rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-3">
                    <div class="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                        <UIcon name="i-heroicons-envelope-open" class="h-3.5 w-3.5" />
                        Sequence preview
                    </div>
                    <div class="space-y-2">
                        <div
                            v-for="step in activeSequencePreview"
                            :key="`${activeIndex}-${step.stepLabel}`"
                            class="rounded-md bg-white px-3 py-2.5 ring-1 ring-blue-100"
                        >
                            <div class="mb-2 flex flex-wrap items-center gap-2">
                                <span class="text-xs font-semibold text-blue-800">{{ step.stepLabel }}</span>
                                <span v-if="step.timing" class="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                                    {{ step.timing }}
                                </span>
                            </div>
                            <div v-if="step.subject" class="mb-2 break-words text-sm text-slate-800">
                                <span class="font-semibold text-slate-500">Subject:</span>
                                {{ step.subject }}
                            </div>
                            <div class="space-y-3 break-words text-sm leading-6 text-slate-800">
                                <p
                                    v-for="(paragraph, index) in copyParagraphs(step.copy)"
                                    :key="`${activeIndex}-${step.stepLabel}-copy-${index}`"
                                    class="whitespace-pre-wrap"
                                >
                                    {{ paragraph }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="activeBet.rationale" class="mt-3 rounded-lg border border-amber-100 bg-amber-50/70 px-3 py-2">
                    <div class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                        Why this might work
                    </div>
                    <p class="text-xs leading-4 text-amber-900">
                        {{ compactRationale(activeBet.rationale) }}
                    </p>
                </div>
            </div>

            <div v-if="hasMultipleBets" class="mt-3 rounded-xl border border-purple-100 bg-white p-3">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <div class="text-sm font-semibold text-slate-900">Compare and build a Step 1 A/B test</div>
                        <div class="text-xs text-slate-500">Select 2–3 eligible first-touch bets. Followups stay unchanged unless separately approved.</div>
                    </div>
                    <div class="flex flex-col items-stretch gap-2 sm:items-end">
                        <div class="flex items-center gap-2">
                            <button
                                type="button"
                                class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                @click="showPreviousBet"
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                class="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700"
                                @click="showNextBet"
                            >
                                Review next creative bet
                                <UIcon name="i-heroicons-arrow-right" class="h-4 w-4" />
                            </button>
                        </div>
                        <button
                            type="button"
                            class="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
                            :class="canDraftAbTest ? 'bg-slate-900 text-white hover:bg-slate-800' : 'cursor-not-allowed bg-slate-100 text-slate-400'"
                            :disabled="!canDraftAbTest"
                            @click="draftStep1AbTest"
                        >
                            Draft Step 1 A/B test with {{ selectedBetCount }} selected
                            <UIcon name="i-heroicons-beaker" class="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <div v-if="selectedVariants.length" class="mt-3 flex flex-wrap gap-1.5">
                    <span
                        v-for="variant in selectedVariants"
                        :key="`${variant.betIndex}-selected`"
                        class="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-[11px] font-semibold text-purple-700 ring-1 ring-purple-100"
                    >
                        <UIcon name="i-heroicons-check" class="h-3 w-3" />
                        {{ variant.bet.label }}
                    </span>
                </div>
                <div class="mt-3 flex gap-1.5">
                    <button
                        v-for="(bet, index) in toolData.bets"
                        :key="`${index}-dot`"
                        type="button"
                        class="h-1.5 rounded-full transition-all"
                        :class="dotClass(index)"
                        :aria-label="`Show creative bet ${index + 1}`"
                        @click="activeIndex = index"
                    />
                </div>
            </div>

            <div v-if="toolData.actions?.length" class="mt-3 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
                <button
                    v-for="action in toolData.actions"
                    :key="action.id"
                    type="button"
                    class="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                    @click="emit('request-variation', action, toolData)"
                >
                    {{ action.label }}
                </button>
            </div>
        </div>

        <div v-else class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            No creative bets available.
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    isStep1AbEligible,
    reconcileSelectedBetIndexes,
    selectedStep1AbVariants,
    toggleStep1AbSelection,
    type CreativeBet,
    type CreativeBetAction,
    type CreativeBetSequencePreviewStep,
    type CreativeBetSequenceStep,
    type CreativeBetsData,
    type Step1CreativeBetVariant,
} from '~/utils/creative-bets';

const props = defineProps<{
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

const emit = defineEmits<{
    'draft-bet': [bet: CreativeBet, data: CreativeBetsData];
    'give-feedback': [bet: CreativeBet, data: CreativeBetsData];
    'draft-step1-ab-test': [variants: Step1CreativeBetVariant[], data: CreativeBetsData];
    'request-variation': [action: CreativeBetAction, data: CreativeBetsData];
}>();

const activeIndex = ref(0);
const selectedBetIndexes = ref<number[]>([]);

const isProcessing = computed(() => {
    if (props.part.output !== undefined) return false;
    if (props.part.errorText) return false;
    return true;
});

const hasError = computed(() => props.part.errorText !== undefined && props.part.errorText !== '');

const toolData = computed(() => {
    const raw = (props.part.output ?? props.part.input) as Partial<CreativeBetsData> | undefined;
    if (!raw || !Array.isArray(raw.bets)) return null;

    return {
        campaignId: Number(raw.campaignId),
        summary: raw.summary || 'Pick a creative direction to improve this campaign.',
        bets: raw.bets.filter(isCreativeBet).slice(0, 3),
        actions: Array.isArray(raw.actions) ? raw.actions.filter(isCreativeBetAction).slice(0, 4) : undefined,
    } satisfies CreativeBetsData;
});

const activeBet = computed(() => toolData.value?.bets[activeIndex.value] ?? toolData.value?.bets[0]);
const hasMultipleBets = computed(() => (toolData.value?.bets.length ?? 0) > 1);
const compactSummary = computed(() => normalizeInlineWhitespace(toolData.value?.summary ?? ''));
const activeSequencePreview = computed(() => activeBet.value ? sequencePreviewForBet(activeBet.value) : []);
const selectedVariants = computed(() => toolData.value ? selectedStep1AbVariants(toolData.value.bets, selectedBetIndexes.value) : []);
const selectedBetCount = computed(() => selectedVariants.value.length);
const canDraftAbTest = computed(() => selectedBetCount.value >= 2 && selectedBetCount.value <= 3);
const isActiveBetSelected = computed(() => selectedBetIndexes.value.includes(activeIndex.value));
const isActiveBetStep1Eligible = computed(() => activeBet.value ? isStep1AbEligible(activeBet.value) : false);

watch(toolData, (data) => {
    if (!data || data.bets.length === 0) {
        activeIndex.value = 0;
        return;
    }
    if (activeIndex.value >= data.bets.length) activeIndex.value = 0;
    selectedBetIndexes.value = reconcileSelectedBetIndexes(data.bets, selectedBetIndexes.value);
});

function showNextBet(): void {
    const count = toolData.value?.bets.length ?? 0;
    if (count <= 1) return;
    activeIndex.value = (activeIndex.value + 1) % count;
}

function showPreviousBet(): void {
    const count = toolData.value?.bets.length ?? 0;
    if (count <= 1) return;
    activeIndex.value = (activeIndex.value - 1 + count) % count;
}

function toggleActiveBetSelection(): void {
    if (!toolData.value) return;
    selectedBetIndexes.value = toggleStep1AbSelection(toolData.value.bets, selectedBetIndexes.value, activeIndex.value);
}

function draftStep1AbTest(): void {
    if (!toolData.value || !canDraftAbTest.value) return;
    emit('draft-step1-ab-test', selectedVariants.value, toolData.value);
}

function dotClass(index: number): string {
    if (index === activeIndex.value) return 'w-7 bg-purple-600';
    if (selectedBetIndexes.value.includes(index)) return 'w-5 bg-purple-300 hover:bg-purple-400';
    return 'w-3 bg-slate-300 hover:bg-slate-400';
}

function sequencePreviewForBet(bet: CreativeBet): CreativeBetSequencePreviewStep[] {
    if (bet.sequencePreview?.length) return bet.sequencePreview.slice(0, 3);
    return [{ stepLabel: fallbackStepLabel(bet.focus), copy: bet.preview }];
}

function fallbackStepLabel(focus: CreativeBet['focus']): string {
    switch (focus) {
        case 'followup-recovery':
            return 'Followup example';
        case 'full-sequence-arc':
        case 'step1-refresh':
        case 'proof-rotation':
        case 'custom':
        default:
            return 'Step 1 example';
    }
}

function copyParagraphs(text: string): string[] {
    return normalizeInlineWhitespace(text)
        .split(/\n{2,}/)
        .map(paragraph => paragraph.trim())
        .filter(Boolean);
}

function compactRationale(text: string): string {
    return normalizeInlineWhitespace(text);
}

function focusLabel(focus: CreativeBet['focus']): string {
    switch (focus) {
        case 'step1-refresh':
            return 'Step 1 refresh';
        case 'full-sequence-arc':
            return 'Full sequence';
        case 'followup-recovery':
            return 'Followup recovery';
        case 'proof-rotation':
            return 'Proof rotation';
        case 'custom':
            return 'Custom';
        default:
            return 'Creative direction';
    }
}

function normalizeInlineWhitespace(text: string): string {
    return text
        .replace(/\\n/g, '\n')
        .replace(/\r\n/g, '\n')
        .trim()
        .split('\n')
        .map(line => line.trim().replace(/[ \t]+/g, ' '))
        .join('\n');
}

function isCreativeBet(value: unknown): value is CreativeBet {
    if (!value || typeof value !== 'object') return false;
    const bet = value as Partial<CreativeBet>;
    if (bet.sequencePreview !== undefined && (!Array.isArray(bet.sequencePreview) || !bet.sequencePreview.every(isCreativeBetSequencePreviewStep))) return false;
    if (bet.sequenceStrategy !== undefined && (!Array.isArray(bet.sequenceStrategy) || !bet.sequenceStrategy.every(isCreativeBetSequenceStep))) return false;
    return typeof bet.id === 'string'
        && typeof bet.label === 'string'
        && typeof bet.preview === 'string';
}

function isCreativeBetSequencePreviewStep(value: unknown): value is CreativeBetSequencePreviewStep {
    if (!value || typeof value !== 'object') return false;
    const step = value as Partial<CreativeBetSequencePreviewStep>;
    return typeof step.stepLabel === 'string'
        && typeof step.copy === 'string'
        && (step.timing === undefined || typeof step.timing === 'string')
        && (step.subject === undefined || typeof step.subject === 'string');
}

function isCreativeBetSequenceStep(value: unknown): value is CreativeBetSequenceStep {
    if (!value || typeof value !== 'object') return false;
    const step = value as Partial<CreativeBetSequenceStep>;
    return typeof step.stepLabel === 'string'
        && typeof step.direction === 'string'
        && (step.cta === undefined || typeof step.cta === 'string')
        && (step.avoidRepeating === undefined || typeof step.avoidRepeating === 'string');
}

function isCreativeBetAction(value: unknown): value is CreativeBetAction {
    if (!value || typeof value !== 'object') return false;
    const action = value as Partial<CreativeBetAction>;
    return typeof action.id === 'string'
        && typeof action.label === 'string'
        && typeof action.prompt === 'string';
}
</script>
